<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import UiCta from '@/components/common/UiCta.vue'
import UiIcon from '@/components/common/UiIcon.vue'
import MobileNavigationItem from '@/components/navigation/MobileNavigationItem.vue'
import { normalizeFormKey } from '@/components/form/registry'
import { useFocusTrap } from '@/composables/useFocusTrap'
import { useHomeFormCtaTarget } from '@/composables/useHomeFormCtaTarget'
import { useDevFormStore } from '@/stores/dev-form.store'
import {
  NAVIGATION_LOCATIONS,
  useNavigationStore,
} from '@/stores/navigation.store'
import { useUIStore } from '@/stores/ui.store'
import { env } from '@/utils/env'
import screeningTests from '../../../screening.json'

type TestOption = {
  value: string
  title: string
  description: string
  type: string
}

type SwitcherOption = {
  value: string | null
  title: string
  description: string
  type: string
}

defineOptions({
  inheritAttrs: false,
})

const attrs = useAttrs()
const { t } = useI18n()
const { homePath, targetEl: homeFormTargetEl } = useHomeFormCtaTarget()
const ui = useUIStore()
const navigationStore = useNavigationStore()
const devFormStore = useDevFormStore()
devFormStore.init()
const { isOffcanvasOpen } = storeToRefs(ui)
const { closeOffcanvas } = ui

const panelRef = ref<HTMLElement | null>(null)
const isPanelContentVisible = ref(false)
let panelContentTimer: number | null = null
let desktopMediaQuery: MediaQueryList | null = null

const navAttrs = computed(() => ({
  id: typeof attrs.id === 'string' ? attrs.id : 'primary-menu',
  role: typeof attrs.role === 'string' ? attrs.role : 'navigation',
  'aria-label': typeof attrs['aria-label'] === 'string'
    ? attrs['aria-label']
    : navigation.value?.name || t('menu.mobileMenu'),
}))

const navigation = computed(() =>
  navigationStore.findByLocation(NAVIGATION_LOCATIONS.mobile)
  ?? navigationStore.findByLocation(NAVIGATION_LOCATIONS.primary),
)

const mobileNavItems = computed(() => navigation.value?.items ?? [])

const testOptions = computed<TestOption[]>(() =>
  screeningTests
    .map((test) => {
      const value = normalizeFormKey(test.key)

      if (!value) {
        return null
      }

      return {
        value,
        title: test.title,
        description: test.description,
        type: test.type,
      }
    })
    .filter((test): test is TestOption => test !== null),
)

const formSwitcherOptions = computed<SwitcherOption[]>(() => [
  {
    value: null,
    title: 'REST response',
    description: 'Use the form key returned by the current endpoint.',
    type: 'api-driven-form',
  },
  ...testOptions.value,
])

const { activate, deactivate } = useFocusTrap(panelRef, {
  escapeDeactivates: false,
  initialFocus: '#mobile-menu-close-button',
  restoreFocus: true,
})

function getEntryClasses() {
  return isPanelContentVisible.value
    ? 'offcanvas-entry offcanvas-entry--visible'
    : 'offcanvas-entry offcanvas-entry--hidden'
}

function getEntryStyle(index: number) {
  if (!isPanelContentVisible.value) return undefined

  return {
    transitionDelay: `${index * 75}ms`,
  }
}

function closeAndResetOffcanvas() {
  closeOffcanvas()
}

function selectFormOption(option: SwitcherOption) {
  devFormStore.setSelectedKey(option.value)
  closeAndResetOffcanvas()
}

function isSelectedFormOption(option: SwitcherOption) {
  return option.value === devFormStore.selectedKey
}

function clearPanelContentTimer() {
  if (panelContentTimer === null || typeof window === 'undefined') return

  window.clearTimeout(panelContentTimer)
  panelContentTimer = null
}

function handlePanelBeforeEnter() {
  isPanelContentVisible.value = false
  clearPanelContentTimer()

  if (typeof window === 'undefined') return

  panelContentTimer = window.setTimeout(() => {
    isPanelContentVisible.value = true
    panelContentTimer = null
  }, 180)
}

function handlePanelAfterEnter() {
  clearPanelContentTimer()
  isPanelContentVisible.value = true
}

function handlePanelBeforeLeave() {
  clearPanelContentTimer()
  isPanelContentVisible.value = false
}

function handleDesktopBreakpoint(event: MediaQueryListEvent | MediaQueryList) {
  if (event.matches) {
    closeAndResetOffcanvas()
  }
}

onMounted(() => {
  if (typeof window === 'undefined') return

  desktopMediaQuery = window.matchMedia('(min-width: 1024px)')
  handleDesktopBreakpoint(desktopMediaQuery)
  desktopMediaQuery.addEventListener('change', handleDesktopBreakpoint)
})

onBeforeUnmount(() => {
  desktopMediaQuery?.removeEventListener('change', handleDesktopBreakpoint)
  clearPanelContentTimer()
  deactivate()
})

watch(isOffcanvasOpen, async (open) => {
  if (open) {
    await nextTick()
    activate()
    return
  }

  deactivate()
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOffcanvasOpen"
        aria-hidden="true"
        class="fixed inset-0 z-50 bg-foreground/25 lg:hidden"
        @click="closeAndResetOffcanvas"
      />
    </Transition>

    <Transition
      name="offcanvas-panel"
      @before-enter="handlePanelBeforeEnter"
      @after-enter="handlePanelAfterEnter"
      @before-leave="handlePanelBeforeLeave"
    >
      <aside
        v-if="isOffcanvasOpen"
        ref="panelRef"
        class="fixed inset-y-0 right-0 z-[60] flex w-full max-w-md lg:hidden"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-menu-heading"
        @click.stop
      >
        <div class="flex h-full w-full flex-col overflow-y-auto bg-background px-5 pb-6 pt-5 shadow-2xl">
          <div class="flex items-center justify-between gap-4 border-b border-border pb-5">
            <!-- <div>
              <h2 id="mobile-menu-heading" class="text-2xl font-semibold text-foreground">
                {{ navAttrs['aria-label'] }}
              </h2>
            </div> -->

            <button
              id="mobile-menu-close-button"
              type="button"
              :aria-label="t('menu.closeMenu')"
              class="btn-ui btn-ui--ghost btn-ui--icon rounded-full text-muted-foreground ml-auto"
              @click="closeAndResetOffcanvas"
            >
              <UiIcon
                name="X"
                class="size-5"
              />
            </button>
          </div>

          <nav
            v-bind="navAttrs"
            :data-navigation-type="navigation?.type"
            class="mt-6 flex flex-col gap-3"
          >
            <ul class="space-y-3">
              <MobileNavigationItem
                v-for="(item, index) in mobileNavItems"
                :key="item.id"
                :item="item"
                :navigation-type="navigation?.type ?? 'normal'"
                :class="getEntryClasses()"
                :style="getEntryStyle(index)"
                @navigate="closeAndResetOffcanvas"
              />
            </ul>
          </nav>

          <section
            v-if="env.isDev"
            class="mt-6 border-t border-border pt-5"
            :class="getEntryClasses()"
            :style="getEntryStyle(mobileNavItems.length)"
            aria-labelledby="mobile-test-switcher-heading"
          >
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-3">
                <UiIcon
                  name="clipboard-clock"
                  class="size-5 shrink-0 text-foreground"
                />
                <h3
                  id="mobile-test-switcher-heading"
                  class="text-sm font-semibold uppercase tracking-[0.18em] text-foreground"
                >
                  Switch rendered test
                </h3>
              </div>
              <span class="text-xs text-muted-foreground">{{ testOptions.length }} tests</span>
            </div>

            <div
              class="mt-4 grid gap-2"
              role="menu"
              aria-labelledby="mobile-test-switcher-heading"
            >
              <button
                v-for="option in formSwitcherOptions"
                :key="option.value ?? 'rest-response'"
                type="button"
                role="menuitemradio"
                class="panel-option items-start"
                :class="{ 'bg-accent text-accent-foreground': isSelectedFormOption(option) }"
                :aria-checked="isSelectedFormOption(option)"
                @click="selectFormOption(option)"
              >
                <span class="mt-1 flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-deep">
                  <UiIcon
                    :name="option.value ? 'clipboard-clock' : 'globe'"
                    class="size-4"
                  />
                </span>
                <span class="min-w-0 flex-1 leading-tight">
                  <span class="flex items-center gap-2">
                    <span class="block truncate text-sm font-semibold">{{ option.title }}</span>
                    <!-- <span
                      v-if="option.value"
                      class="status-pill shrink-0 normal-case tracking-normal"
                    >
                      {{ option.value }}
                    </span> -->
                  </span>
                  <span class="mt-1 block text-xs text-muted-foreground">{{ option.description }}</span>
                  <span class="mt-1 block truncate text-[11px] text-muted-foreground/80">{{ option.type }}</span>
                </span>
                <!-- <UiIcon
                  v-if="isSelectedFormOption(option)"
                  name="check"
                  class="mt-2 size-3.5 shrink-0 text-muted-foreground"
                /> -->
              </button>
            </div>
          </section>

          <div
            class="mt-6 border-t border-border pt-5"
            :class="getEntryClasses()"
            :style="getEntryStyle(mobileNavItems.length + (env.isDev ? 1 : 0))"
          >
            <UiCta
              class="w-full justify-center"
              :options="{
                type: 'scroll',
                to: homePath,
                text: t('cta.survey'),
                color: 'primary',
                targetEl: homeFormTargetEl
              }"
              @click.capture="closeAndResetOffcanvas"
            />
          </div>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>
